import { Button, orderNoShort } from '@cuito/ui';
import type { OrderStatus } from '@cuito/types';
import { geomAttrs } from '../lib/boardGeometry';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';
import type { OrderQueueItem } from '../api/orders';

/**
 * 订单卡（§3.3 `/cashier` 的关键组件；锚点 `order-card` 是冻结接口）。
 *
 * 内容五件套：订单号前 8 位 · 短码 · 件数 · 金额 · 状态徽标，外加未读点。
 *
 * ⚠️ **金额显示口径**（§7.8）：前缀 `¥` + 两位小数 + 千分位 ⇒ 走
 * `lib/schemeForm.formatCents`（**就是** `@cuito/ui` 的 `formatYuan`，Phase 5 Task 22 收口），
 * 不要在这里写 `toFixed(2)`（那会丢掉千分位，是 Phase 4 链 E 审出的用户可见项）。
 * 服务端金额读不出来时渲染 `AMOUNT_UNAVAILABLE_TEXT`，**绝不显示编造的 `¥0.00`**（附录 B1s）。
 *
 * ⚠️ **件数取 `itemCount`（`Σ lines[].quantity`），不是 `lineCount`（行数）**：
 * §7.8 的单位是「商品用 `件`」，一张 2 行 3 件的单必须显示「3 件」；`itemCount`
 * 由 `orderQueue` 的行派生（载荷缺 `lines` 时沿用上一份快照），是队列项上的真值字段。
 *
 * ⚠️ **订单号显示前 8 位**（§7.8 于 2026-09-30 统一）：走 `@cuito/ui` 的 `orderNoShort`
 * （唯一实现）。本文件曾用 `orderNo.slice(-6)`，与顾客站的 `slice(0, 8)` 冲突
 * （附录 B1k / B1k-bis / B1k-ter）—— 五处现已同源，见
 * `apps/tenant/src/cashier/orderNoDisplay.spec.tsx`。
 */
export const ORDER_STATUS_TEXT: Record<OrderStatus, string> = {
  PENDING: '待接单',
  ACCEPTED: '已接单',
  PICKING: '挑拣中',
  READY: '可交付',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
  REFUNDED: '已退款',
};

/** 状态徽标的配色（Task 15 的订单头复用同一张表，避免「同一状态两种颜色」） */
export const ORDER_STATUS_CLASS: Record<OrderStatus, string> = {
  PENDING: 'border-black bg-white text-black',
  ACCEPTED: 'border-black bg-gray-100 text-black',
  PICKING: 'border-black bg-gray-100 text-black',
  READY: 'border-black bg-black text-white',
  COMPLETED: 'border-gray-400 bg-white text-gray-500',
  CANCELLED: 'border-gray-400 bg-white text-gray-500',
  REFUNDED: 'border-gray-400 bg-white text-gray-500',
};

interface Props {
  order: OrderQueueItem;
  /** 未读点（§6.2）：新订单插入时挂上，接单后消失 */
  unread: boolean;
  /** 新插入的 2 秒高亮（§6.2：`ring-2 ring-black animate-pulse`） */
  highlight: boolean;
  accepting: boolean;
  onAccept: () => void;
  onOpen: () => void;
}

export default function OrderCard({ order, unread, highlight, accepting, onAccept, onOpen }: Props) {
  return (
    <article
      data-testid="order-card"
      data-order-no={order.orderNo}
      className={`flex flex-col gap-2 rounded-lg border-2 border-black p-3 ${
        highlight ? 'ring-2 ring-black animate-pulse' : ''
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {unread && (
          <span
            data-testid="order-card-unread"
            aria-label="未读"
            className="h-2 w-2 rounded-full bg-black"
          />
        )}
        <span className="text-base font-bold">订单 {orderNoShort(order.orderNo)}</span>
        <span className="text-base font-bold">短码 {order.shortCode}</span>
        <span
          data-testid="order-status-badge"
          className={`ml-auto rounded border px-1.5 py-0.5 text-xs font-bold ${ORDER_STATUS_CLASS[order.status]}`}
        >
          {ORDER_STATUS_TEXT[order.status]}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">{order.itemCount} 件</span>
        <span className="text-lg font-bold">
          {formatServerYuan(order.totalAmount) ?? AMOUNT_UNAVAILABLE_TEXT}
        </span>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onOpen}>
          查看
        </Button>
        {order.status === 'PENDING' && (
          /*
           * `data-geom="accept-button"`：把「接单按钮的纵向高度」交给布局模型
           * （见 `lib/boardGeometry.ts`）。它是 Task 24「第一次点接单被吃掉」的判据落点：
           * 解锁提示条插进列表上方 ⇒ 这个按钮的 y 变了 ⇒ 同一次点击的 pointerup 落到别处。
           */
          <Button onClick={onAccept} disabled={accepting} {...geomAttrs('accept-button')}>
            {accepting ? '接单中…' : '接单'}
          </Button>
        )}
      </div>
    </article>
  );
}
